import { m } from 'framer-motion';
import { useState, useEffect, useCallback } from 'react';
// @mui
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
// routes
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hook';
// hooks
// import { useMockedUser } from 'src/hooks/use-mocked-user';
import { useAuthContext } from 'src/auth/hooks';
import { useSession } from 'next-auth/react';
import { usePathname } from 'src/routes/hook';
// components
import { varHover } from 'src/components/animate';
import { useSnackbar } from 'src/components/snackbar';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
// utils
import axiosInstance from 'src/utils/axios';

// ----------------------------------------------------------------------

const OPTIONS = [
  {
    label: 'Billing',
    linkTo: paths.dashboard.user.account,
  },
  {
    label: 'Refer a Friend',
    linkTo: paths.dashboard.user.affiliate,
  },
];

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const router = useRouter();
  const pathname = usePathname();
  const isDemo = pathname.startsWith('/demo');

  // const { user } = useMockedUser();

  const { logout, user } = useAuthContext();
  const { data: session } = useSession();

  const demoUser = isDemo ? {
    name: 'Demo User',
    email: 'demo@storefrontstalker.com',
    image: undefined,
    role: 'demo'
  } : null;

  const displayUser = isDemo ? demoUser : user;
  const isVirtualAgent = session?.user?.role === 'virtual_agent';

  const { enqueueSnackbar } = useSnackbar();

  const popover = usePopover();

  const [emailEnabled, setEmailEnabled] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);

  const fetchEmailStatus = useCallback(async () => {
    if (!session?.user?.email) return;
    try {
      const response = await axiosInstance.post('/users/sendEmail/status', {
        email: session.user.email,
      });
      if (!response.data.error && typeof response.data.sendEmail === 'boolean') {
        setEmailEnabled(response.data.sendEmail);
      }
    } catch (error) {
      console.error('Failed to fetch email status:', error);
    }
  }, [session?.user?.email]);

  // Fetch on mount when session is available
  useEffect(() => {
    if (session?.user?.email) {
      fetchEmailStatus();
    }
  }, [session?.user?.email, fetchEmailStatus]);

  // Also fetch when popover opens to get latest status
  useEffect(() => {
    if (popover.open && session?.user?.email) {
      fetchEmailStatus();
    }
  }, [popover.open, session?.user?.email, fetchEmailStatus]);

  const handleEmailToggle = async () => {
    if (!session?.user?.email || emailLoading) return;
    setEmailLoading(true);
    try {
      const newValue = !emailEnabled;
      const response = await axiosInstance.post('/users/sendEmail/update', {
        email: session.user.email,
        sendEmail: newValue,
      });
      if (!response.data.error) {
        setEmailEnabled(response.data.sendEmail);
        enqueueSnackbar(`Email notifications ${newValue ? 'enabled' : 'disabled'}`, {
          variant: 'success',
        });
      }
    } catch (error) {
      console.error('Failed to update email status:', error);
      enqueueSnackbar('Failed to update email settings', { variant: 'error' });
    } finally {
      setEmailLoading(false);
    }
  };

  const handleLogout = async () => {
    if (isDemo) {
      router.replace('/');
      return;
    }
    
    try {
      await logout();
      popover.onClose();
      // router.replace('/');
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  };

  const handleClickItem = (path: string) => {
    //open in new tab
    window.open(path, '_blank');
    popover.onClose();
/*     router.push(path);
 */  };

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        onClick={popover.onOpen}
        sx={{
          width: 40,
          height: 40,
          background: (theme) => alpha(theme.palette.grey[500], 0.08),
          ...(popover.open && {
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
          }),
        }}
      >
        <Avatar
          src={displayUser?.image}
          alt={displayUser?.displayName || displayUser?.name}
          sx={{
            width: 36,
            height: 36,
            border: (theme) => `solid 2px ${theme.palette.background.default}`,
          }}
        />
      </IconButton>

      <CustomPopover open={popover.open} onClose={popover.onClose} sx={{ width: 200, p: 0 }}>
        <Box sx={{ p: 2, pb: 1.5 }}>
          <Typography variant="subtitle2" noWrap>
            {displayUser?.name || displayUser?.displayName}
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {displayUser?.email}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        {!isVirtualAgent && !isDemo && (
          <>
            <Stack sx={{ p: 1 }}>
              {OPTIONS.map((option) => (
                <MenuItem key={option.label} onClick={() => handleClickItem(option.linkTo)}>
                  {option.label}
                </MenuItem>
              ))}
            </Stack>

            <Divider sx={{ borderStyle: 'dashed' }} />
          </>
        )}

        {!isVirtualAgent && !isDemo && (
          <>
            <Box sx={{ px: 2, py: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={emailEnabled}
                    onChange={handleEmailToggle}
                    disabled={emailLoading}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2">
                    Enable Email
                  </Typography>
                }
              />
            </Box>

            <Divider sx={{ borderStyle: 'dashed' }} />
          </>
        )}

        <MenuItem
          onClick={handleLogout}
          sx={{ m: 1, fontWeight: 'fontWeightBold', color: 'error.main' }}
        >
          {isDemo ? 'Exit Demo' : 'Logout'}
        </MenuItem>
      </CustomPopover>
    </>
  );
}
